import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTexte } from '../../texte'
import { Blattkopf } from '../../components/Blattkopf'
import { useEscape } from '../../hooks/useEscape'
import { useSettings, useTrip } from '../../hooks/queries'
import { useAttachments } from '../../hooks/useAttachments'
import { isImage } from '../../lib/images'
import { COLLAGE_FEWEST, COLLAGE_MOST, fittingLayout, newCollageStyle, type CollageStyle } from '../../lib/collage'
import type { Uhr } from '../../lib/ortszeit'
import type { Message } from '../../api/types'
import { Collage } from './Collage'
import { CollageStyleFields } from './CollageStyleFields'

/**
 * The sheet of a collage (1.168.0): pick two to four photos, choose the
 * layout, corners and gap, see it, send it -- and later the same sheet to
 * change it. From use: "Vorschau vor dem Versenden", and "im Änderungsmodus:
 * Änderung der Parameter, Bilder neu hinzufügen bzw. Löschen".
 *
 * **The photos go the chat's way** (`useAttachments`): shrunk, uploaded,
 * with the tile's stage, the same as a photo in the line -- its own instance,
 * so the line's tiles stay the line's. In the change, the post's photos come
 * in as pictures already on the server (`nimmAusReise`): the cross only
 * takes them out of the list, and the server lets go of them on saving.
 *
 * **The picker is opened by the button in the sheet**, inside the tap; iOS
 * opens a file picker only from a gesture, and a sheet that opened it by
 * itself after mounting would get nothing.
 *
 * What the sheet hands back is the list of ids in order and the style; who
 * sends or saves is the caller's (the line with its key and place, the page
 * with the change).
 */
export function CollageSheet({
  tripId,
  reiseVersatz,
  message,
  laeuft,
  fehler,
  onFertig,
  onZu,
}: {
  tripId: number
  reiseVersatz: Uhr
  /** The collage being changed; absent for a new one. */
  message?: Message
  laeuft: boolean
  fehler: string | null
  onFertig: (mediaIds: number[], style: CollageStyle) => void
  onZu: () => void
}) {
  const t = useTexte()
  const words = t.chat.collage
  const settings = useSettings()
  const choices = settings.data?.collage
  const werte = settings.data?.settings
  const trip = useTrip(tripId)
  const anhang = useAttachments({ tripId, reiseVersatz })
  const picker = useRef<HTMLInputElement>(null)

  // **Cancelled, the new uploads go** -- they would count against the trip
  // until they expire. The post's own photos stay where they are
  // (`bestand`); after sending, the caller closes the sheet, not this.
  function abbrechen() {
    for (const foto of anhang.fotos) {
      if (foto.bestand !== true) anhang.entferne(foto)
    }
    onZu()
  }
  useEscape(abbrechen)

  // **The defaults are read while drawing, not copied at mounting**: the
  // settings may still be on their way when the sheet opens, and a copy
  // would keep the built-in numbers instead of the administration's. Once
  // someone touches a control, their style is the one.
  //
  // **The trip's values first** (1.172.0): a trip carries its own defaults,
  // the administration's are where it started (`newCollageStyle()`).
  const [eigener, setStyle] = useState<CollageStyle | null>(message?.collage ?? null)
  const style: CollageStyle = eigener ?? {
    layout: '',
    ...newCollageStyle(trip.data?.post_defaults, werte),
  }

  // The post's own photos as tiles that are already there (the change).
  const geladen = useRef(false)
  useEffect(() => {
    if (geladen.current || message === undefined) return
    geladen.current = true
    anhang.nimmAusReise(message.media.filter((m) => m.kind === 'image'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  const fotos = anhang.fotos
  const count = fotos.length
  const layouts = choices?.layouts[String(count)] ?? []
  // The layout that fits the number now: after a photo came or went the
  // chosen one may not (`fittingLayout`).
  const shown: CollageStyle = { ...style, layout: fittingLayout(choices?.layouts ?? {}, count, style.layout) }
  const most = choices?.most ?? COLLAGE_MOST
  const fewest = choices?.fewest ?? COLLAGE_FEWEST
  const bereit = count >= fewest && count <= most && fotos.every((foto) => foto.stufe === 'fertig' && foto.id !== undefined)

  function nimm(files: FileList | null) {
    if (!files) return
    // Photos only: `accept` is no lock (CLAUDE.md), so a video picked from
    // the Files app stays out here, and the server refuses it anyway.
    const bilder = [...files].filter((file) => isImage(file)).slice(0, Math.max(0, most - count))
    if (bilder.length > 0) void anhang.add(bilder)
  }

  // **The order is changed in the preview** (1.169.0), from use: "Die
  // Reihenfolge der Fotos lässt sich aber nicht ändern". One tap marks a
  // photo, a tap on another swaps the two; the first is the big one. A swap
  // and not dragging: a finger dragging in a sheet scrolls the sheet. The
  // server takes the order of the ids as it comes (`media.position`).
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const markiert = fotos.some((foto) => foto.key === gewaehlt) ? gewaehlt : null
  function tippe(key: number) {
    if (markiert === null || markiert === key) {
      setGewaehlt(markiert === key ? null : key)
      return
    }
    anhang.setFotos((alle) => {
      const a = alle.findIndex((foto) => foto.key === markiert)
      const b = alle.findIndex((foto) => foto.key === key)
      if (a < 0 || b < 0) return alle
      return alle.map((foto, i) => (i === a ? alle[b]! : i === b ? alle[a]! : foto))
    })
    setGewaehlt(null)
  }

  const hinweis = count > most ? words.zuViele : count < fewest ? words.zuWenige : anhang.beschaeftigt ? words.laedt : null

  return createPortal(
    <div className="bogen" role="dialog" aria-modal="true" aria-label={message ? words.aendernTitel : words.titel} data-collageblatt="">
      <div className="bogen__blatt bogen__blatt--voll bogen__blatt--gegliedert">
        <Blattkopf titel={message ? words.aendernTitel : words.titel} onSchliessen={abbrechen} />

        {/* **The preview stands above the controls, not among them**: in
            the scrolling middle it went out of sight exactly while the gap
            was being turned (seen in `collage.mjs`). A sibling of head and
            middle, like the foot -- not sticky inside the box it covers. */}
        {count > 0 && (
          <div className="collageblatt__vorschau collageblatt__vorschau--blatt" data-collage-vorschau="">
            <Collage
              style={shown}
              tiles={fotos.map((foto, i) => (
                <button
                  type="button"
                  className="collageblatt__tausch"
                  aria-pressed={foto.key === markiert}
                  aria-label={words.tauschen(i + 1)}
                  onClick={() => tippe(foto.key)}
                  data-collage-tausch={i}
                >
                  {foto.preview ? <img src={foto.preview} alt="" /> : <span className="collageblatt__leer" />}
                  {/* The mark is the last child and not an inset shadow: a
                      shadow is painted under the picture (CLAUDE.md). */}
                  <span className="collageblatt__marke" aria-hidden="true" />
                </button>
              ))}
            />
          </div>
        )}
        {count > 1 && (
          <p className="feld__hinweis collageblatt__tauschhinweis" data-collage-tauschhinweis="">
            {words.tauschHinweis}
          </p>
        )}

        <div className="bogen__rolle">

          <div className="collageblatt__fotos">
            {fotos.map((foto, i) => (
              <span key={foto.key} className="collageblatt__foto">
                {foto.preview ? <img src={foto.preview} alt="" /> : <span className="collageblatt__leer" />}
                <button
                  type="button"
                  className="collageblatt__weg"
                  aria-label={words.fotoEntfernen(i + 1)}
                  onClick={() => anhang.entferne(foto)}
                  data-collage-weg={i}
                >
                  ×
                </button>
              </span>
            ))}
            {count < most && (
              <button
                type="button"
                className="button button--outline button--small collageblatt__dazu"
                onClick={() => picker.current?.click()}
                data-collage-dazu=""
              >
                {count === 0 ? words.fotosWaehlen : words.fotoDazu}
              </button>
            )}
            <input
              ref={picker}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => {
                nimm(event.target.files)
                event.target.value = ''
              }}
              data-collage-picker=""
            />
          </div>

          {hinweis !== null && (
            <p className="feld__hinweis" data-collage-hinweis="">
              {hinweis}
            </p>
          )}

          <CollageStyleFields
            style={shown}
            layouts={layouts}
            bounds={{ radius: choices?.max_radius ?? 24, gap: choices?.max_gap ?? 16 }}
            onChange={setStyle}
          />

          {(fehler ?? anhang.problem) !== null && (
            <p className="notice" role="alert" data-collage-fehler="">
              {fehler ?? anhang.problem}
            </p>
          )}
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={!bereit || laeuft}
            onClick={() => onFertig(fotos.map((foto) => foto.id!), shown)}
            data-collage-senden=""
          >
            {laeuft ? t.allgemein.laeuft : message ? t.allgemein.speichern : words.senden}
          </button>
          <button type="button" className="button button--outline" onClick={abbrechen}>
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
